// Completions state. EVERY write — done/missed, reopen, note edit, photo — goes
// through the durable queue (ADR-0008): recorded in IndexedDB first, sent when the
// network allows, retried with the same idempotency key. The UI is optimistic; server
// truth replaces it on drain, and rejected ops surface in the attention sheet.
import { signal } from '@preact/signals';
import { apiGet } from '../lib/api';
import { resizeImage } from '../lib/image';
import { drain, enqueue, initQueue, wireQueue } from '../lib/queue';
import { techName } from './session';
import { toast } from './toast';

export interface CompletionRec {
  by: string;
  at: string;
  note: string;
  needsReturn: boolean;
  status: 'done' | 'missed';
  photos: string[];
}

export const completions = signal<Record<string, CompletionRec>>({});
export const parked = signal<Set<string>>(new Set());

export async function refreshCompletions(): Promise<void> {
  const [comps, park] = await Promise.all([
    apiGet<Record<string, CompletionRec>>('/api/completions'),
    apiGet<{ ids: Record<string, boolean> }>('/api/parked'),
  ]);
  completions.value = comps;
  parked.value = new Set(Object.keys(park.ids));
}

// Queue wiring: successful completion drains push server truth here; settled events
// (conflict drop, dead-letter, discard) re-sync the board so optimism never lingers.
wireQueue(
  (map) => {
    completions.value = map as Record<string, CompletionRec>;
  },
  () => void refreshCompletions().catch(() => {}),
);
initQueue();

interface ResolveInput {
  id: string;
  status: 'done' | 'missed';
  note: string;
  needsReturn: boolean;
}

/** Mark a call done/missed. Durable the moment it resolves; syncs when possible. */
export async function resolveCall(input: ResolveInput): Promise<boolean> {
  const at = new Date().toISOString();
  const rec: CompletionRec = {
    by: techName.value,
    at,
    note: input.note,
    needsReturn: input.needsReturn,
    status: input.status,
    photos: [],
  };
  completions.value = { ...completions.value, [input.id]: rec }; // optimistic
  await enqueue({
    opId: crypto.randomUUID(),
    kind: 'completion',
    callId: input.id,
    label: `${input.status === 'missed' ? 'Missed' : 'Done'} — ${input.note || 'no note'}`,
    body: {
      action: 'complete',
      id: input.id,
      status: input.status,
      by: rec.by,
      at,
      note: rec.note,
      needsReturn: rec.needsReturn,
    },
  });
  if (!navigator.onLine) toast('Saved on this phone — will sync when you have signal.');
  return true;
}

/** Tech reopen of a MISSED call (the reschedule flow). */
export async function reopenMissed(id: string): Promise<boolean> {
  const prev = completions.value;
  const rest = { ...prev };
  delete rest[id];
  completions.value = rest; // optimistic
  await enqueue({
    opId: crypto.randomUUID(),
    kind: 'completion',
    callId: id,
    label: 'Reopen missed appointment',
    body: { action: 'reopen', id },
  });
  return true;
}

export async function saveNote(id: string, note: string): Promise<void> {
  const prev = completions.value[id];
  if (prev) completions.value = { ...completions.value, [id]: { ...prev, note } };
  await enqueue({
    opId: crypto.randomUUID(),
    kind: 'completion',
    callId: id,
    label: `Note edit — ${note.slice(0, 40)}`,
    body: { action: 'editNote', id, note },
  });
}

/**
 * Queue photos for a call. Each file is resized and captured to IndexedDB BEFORE any
 * upload attempt — capture and upload are separate paths (§7); a photo is never at
 * risk because an upload failed. A bad file doesn't abort the rest (v103 lesson).
 */
export async function uploadPhotos(id: string, files: File[]): Promise<void> {
  let bad = 0;
  for (const f of files) {
    try {
      const blob = await resizeImage(f);
      await enqueue({
        opId: crypto.randomUUID(),
        kind: 'photo',
        callId: id,
        label: `Photo for job ${id}`,
        blob,
      });
    } catch {
      bad++; // unreadable file — not a network problem, nothing to queue
    }
  }
  if (bad) toast(`${bad} file${bad > 1 ? 's' : ''} could not be read.`);
  // Thumbnails appear once the drain lands; nudge a sync shortly after.
  setTimeout(() => {
    void drain();
    void refreshCompletions().catch(() => {});
  }, 1500);
}
